import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import JobCard from "@/components/JobCard";
import { useAuth } from "@/contexts/AuthContext";
import { Lock } from "lucide-react";

const REGIONS = ["All", "Europe", "Americas", "Asia Pacific"];
const SENIORITIES = ["All", "Executive", "Senior", "Mid"];
const BOUNTY_FLOORS = [
    { label: "Any bounty", value: 0 },
    { label: "$15k+", value: 15000 },
    { label: "$20k+", value: 20000 },
];

const VIEWER_COPY = {
    anonymous: {
        title: "Sign in to unlock real targets",
        body: "12 active Security Strikes and Control Audits are gated. Sign in & complete the Trinity Boarding (Director Interview → First Light fee → Mission Intake) to view risk scores, vulnerabilities, and real company names.",
        primary: { label: "Sign in", to: "/login" },
        secondary: { label: "Become a Privateer", to: "/foundry/interview" },
    },
    authenticated_unboarded: {
        title: "Complete boarding to unlock intel",
        body: "You're signed in but not yet boarded. Complete the Director Interview and the $250 First Light fee, then run Mission Intake to clear the gate.",
        primary: { label: "Start Boarding", to: "/foundry/interview" },
        secondary: null,
    },
    boarded_no_intake: {
        title: "One step left — Mission Intake",
        body: "Boarded but un-cleared. Complete the Mission Intake (skills + NDA) to unlock real B2B dossiers.",
        primary: { label: "Run Mission Intake", to: "/foundry/mission-intake" },
        secondary: null,
    },
};

export default function JobsFeed() {
    const { user, loading: authLoading } = useAuth();
    const { search } = useLocation();
    const [region, setRegion] = useState("All");
    const [seniority, setSeniority] = useState("All");
    const [minBounty, setMinBounty] = useState(0);
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [viewerState, setViewerState] = useState("anonymous");
    const [cleared, setCleared] = useState(false);
    const [lockedCount, setLockedCount] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);
        const params = {};
        if (region !== "All") params.region = region;
        if (seniority !== "All") params.seniority = seniority;
        if (minBounty) params.min_bounty = minBounty;
        if (q.trim()) params.q = q.trim();
        api.get("/jobs", { params })
            .then((r) => {
                if (!active) return;
                const data = r.data || {};
                setItems(Array.isArray(data) ? data : (data.items || []));
                setViewerState(data.viewer_state || (user ? "authenticated_unboarded" : "anonymous"));
                setCleared(!!data.cleared);
                setLockedCount(Number(data.locked_count || 0));
            })
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [region, seniority, minBounty, q, user]);

    const totalBounty = useMemo(
        () => items.reduce((acc, j) => acc + (j.bounty || j.referral_bounty || 0), 0),
        [items]
    );

    const copy = !cleared && lockedCount > 0 ? VIEWER_COPY[viewerState] : null;

    return (
        <div data-testid="jobs-feed-page" className="mx-auto max-w-[1400px] px-6 py-16 md:px-10">
            <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
                <div>
                    <div className="caption text-[var(--brand-accent)]">Global Opportunity Exchange</div>
                    <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA] sm:text-6xl">
                        Active Missions
                    </h1>
                    <p className="mt-3 max-w-xl text-[#A3A3A3]">
                        {items.length} live missions · ${(totalBounty / 1000).toFixed(0)}k in placement & referral bounties on offer.
                        {lockedCount > 0 && !cleared && (
                            <span className="ml-2 text-[var(--brand-accent)]">
                                · {lockedCount} locked
                            </span>
                        )}
                    </p>
                </div>
                <input
                    data-testid="input-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search role, company, city…"
                    className="w-full max-w-md rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] placeholder:text-white/30 transition-all duration-300 focus:border-[var(--brand-accent)] focus:bg-white/[0.05] focus:outline-none"
                />
            </div>

            {copy && (
                <div
                    data-testid="locked-hull-banner"
                    data-viewer-state={viewerState}
                    className="mb-10 flex flex-col gap-5 rounded-md border p-7 md:flex-row md:items-center md:justify-between md:gap-10"
                    style={{
                        borderColor: "rgba(201,169,110,0.3)",
                        background: "rgba(201,169,110,0.04)",
                    }}
                >
                    <div className="flex items-start gap-4">
                        <div
                            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border"
                            style={{
                                borderColor: "rgba(201,169,110,0.4)",
                                color: "var(--brand-accent)",
                            }}
                        >
                            <Lock size={20} />
                        </div>
                        <div>
                            <div className="caption text-[var(--brand-accent)]">
                                {lockedCount} Locked Dossier{lockedCount === 1 ? "" : "s"} · Viewer: {viewerState.replace(/_/g, " ")}
                            </div>
                            <h2 className="mt-2 font-serif text-2xl tracking-display text-[#FAFAFA] md:text-3xl">
                                {copy.title}
                            </h2>
                            <p className="mt-2 max-w-2xl text-sm text-[#A3A3A3]">{copy.body}</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            to={{ pathname: copy.primary.to, search }}
                            data-testid="locked-banner-primary"
                            className="inline-flex items-center justify-center rounded-sm px-6 py-3 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.35)]"
                            style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                        >
                            {copy.primary.label}
                        </Link>
                        {copy.secondary && (
                            <Link
                                to={{ pathname: copy.secondary.to, search }}
                                data-testid="locked-banner-secondary"
                                className="inline-flex items-center justify-center rounded-sm border border-white/15 px-6 py-3 text-sm font-medium text-[#FAFAFA] hover:bg-white/[0.04]"
                            >
                                {copy.secondary.label}
                            </Link>
                        )}
                    </div>
                </div>
            )}

            <div className="mb-10 flex flex-wrap gap-x-8 gap-y-4">
                <FilterGroup label="Region" value={region} onChange={setRegion} options={REGIONS} testKey="region" />
                <FilterGroup label="Seniority" value={seniority} onChange={setSeniority} options={SENIORITIES} testKey="seniority" />
                <FilterGroup
                    label="Bounty"
                    value={minBounty}
                    onChange={setMinBounty}
                    options={BOUNTY_FLOORS.map((b) => b.value)}
                    labelMap={Object.fromEntries(BOUNTY_FLOORS.map((b) => [b.value, b.label]))}
                    testKey="bounty"
                />
            </div>

            {loading || authLoading ? (
                <div className="text-center text-[#A3A3A3] py-24">Curating mandates…</div>
            ) : items.length === 0 ? (
                <div data-testid="no-results" className="text-center text-[#A3A3A3] py-24 font-serif text-2xl">
                    No missions match your criteria.
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="jobs-grid">
                    {items.map((j) => <JobCard key={j.id} job={j} />)}
                </div>
            )}
        </div>
    );
}

function FilterGroup({ label, value, onChange, options, labelMap, testKey }) {
    return (
        <div>
            <div className="caption mb-2 text-[#737373]">{label}</div>
            <div className="flex flex-wrap gap-2">
                {options.map((opt) => {
                    const active = value === opt;
                    const text = labelMap ? labelMap[opt] : String(opt);
                    return (
                        <button
                            key={String(opt)}
                            data-testid={`filter-${testKey}-${opt}`}
                            data-state={active ? "active" : "inactive"}
                            onClick={() => onChange(opt)}
                            className={`inline-flex cursor-pointer items-center justify-center rounded-full border px-4 py-1.5 text-xs tracking-wider transition-all duration-300 ${active ? "" : "border-white/10 bg-transparent text-[#A3A3A3] hover:border-white/30 hover:text-[#FAFAFA]"}`}
                            style={active ? {
                                borderColor: "var(--brand-accent)",
                                background: "rgba(201,169,110,0.10)",
                                color: "var(--brand-accent)",
                            } : undefined}
                        >
                            {text}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
